/**
 * BrandWordmark — the official prest0 lockup: "prest" + the logo mark as the
 * zero, exactly as the home-page header renders it (2026-08-04 owner
 * directive: every onboarding surface must carry the same lockup, never the
 * plain-text "prest0"). Same asset + inline metrics as Header.tsx.
 *
 * tone="onDark" (2026-08-20 owner directive: "prest0 should not be black" on
 * the black org-transition screen): light text + the Mission-page dark-surface
 * glow on the mark. Default tone is byte-identical to the original — every
 * existing light-surface consumer renders unchanged.
 */

import React from 'react';
import styled from 'styled-components';

const InlineLogo = styled.img<{ $onDark?: boolean }>`
  display: inline-block;
  height: 1.1em;
  margin-left: -0.12em;
  margin-bottom: -0.15em;
  vertical-align: baseline;
  object-fit: contain;
  filter: ${(p) =>
    p.$onDark
      ? 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.15)) drop-shadow(0 0 4px rgba(255, 255, 255, 0.18))'
      : 'drop-shadow(0 2px 6px rgba(200, 167, 94, 0.15))'};
`;

const Mark = styled.a<{ $onDark?: boolean }>`
  display: inline-block;
  line-height: 1;
  /* The img-zero must never wrap beneath "prest" under flex squeeze. */
  white-space: nowrap;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: ${(p) => (p.$onDark ? 'var(--brand-canvas)' : 'var(--brand-text)')};
  text-decoration: none;
`;

export const BrandWordmark: React.FC<{
  href?: string;
  className?: string;
  /**
   * Render the lockup as plain (non-link) text. Focused funnels — the
   * invitation landing page (owner directive 2026-08-12) — show the brand
   * for trust but must not offer an exit: no href, no pointer cursor.
   */
  nonNavigating?: boolean;
  /** 'onDark' = light lockup for black surfaces (org-transition overlay). */
  tone?: 'default' | 'onDark';
}> = ({ href = '/', className, nonNavigating = false, tone = 'default' }) =>
  nonNavigating ? (
    <Mark
      as="span"
      className={className}
      aria-label="prest0"
      style={{ cursor: 'default' }}
      $onDark={tone === 'onDark'}
    >
      prest
      <InlineLogo
        src="/images/project-olivia-logo.webp?v=20260715"
        alt="0"
        $onDark={tone === 'onDark'}
      />
    </Mark>
  ) : (
    <Mark href={href} className={className} aria-label="prest0" $onDark={tone === 'onDark'}>
      prest
      <InlineLogo
        src="/images/project-olivia-logo.webp?v=20260715"
        alt="0"
        $onDark={tone === 'onDark'}
      />
    </Mark>
  );

export default BrandWordmark;
